import { ReactComponent as IconDelete } from "@/assets/svgs/Delete.svg";
import AttachmentDropzone from "@/components/AttachmentDropzone/index";
import Button from '@/components/Button/index';
import ModalTemplate from "@/components/ModalTemplate/index";
import Portal from '@/components/Portal';
import Toast from "@/components/Toast/index";
import useModal from "@/hooks/use-modal/index";
import usePage from '@/hooks/use-page';
import usePrincipals from "@/hooks/use-principal/index";
import useToast from "@/hooks/use-toast/index";
import { useNavigate } from "@/lib/router-dom/index";
import { convertCSVtoJSON, convertJsonToCSV } from "@/utils/csvtojson";
import validateUEN, { validatePrincipalUEN } from "@/utils/validateUEN";
import FileSaver from "file-saver";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from 'react';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import { useFeature } from "@/hooks/use-feature/index";
import { featureConstant } from "@/constants/configuration/feature";

const INPUT = {
	MANUAL: 'manual',
	BULK: 'bulk',
}

const CreatePrincipal = ({ input }) => {

	/*=================================================
		General
	/================================================*/

	const [pageTitle] = useState('Add New Principal');

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Use Cases',
				link: `/use-cases/principal`
			},
			{
				label: 'Add New Principal',
				link: ``
			},
		]

	});

	const { goTo } = useNavigate();

	const [principalName, setPrincipalName] = useState("");
	const [uen, setUEN] = useState("");
	const [newPrincipals, setNewPrincipals] = useState([]);
	const [internalError, setInternalError] = useState(false);
	const [bulkUploadFile, setBulkUploadFile] = useState(false);

	/*=================================================
		Toast
	/================================================*/

	const { toastList, addToastMessage } = useToast();

	/*=================================================
		Principal
	/================================================*/

	const { create, find, loading, error: errorPrincipal } = usePrincipals();

	useEffect(() => {
		addToastMessage(errorPrincipal, 'error');
	}, [errorPrincipal])

	/*=================================================
		Principal Management
	/================================================*/

	const isAgentPrincipalPhase2Enabled = useFeature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	async function handleSubmit() {
		if (loading)
			return

		const inputUen = uen?.trim();

		const foundResult = await find({ uen: inputUen })

		if (foundResult?.length > 0) {
			addToastMessage('UEN already registered', 'error');
			return
		}

		if (newPrincipals?.find(s => s.uen == inputUen)) {
			addToastMessage('UEN already added in table', 'error');
			return
		}

		const array = [...newPrincipals];
		array.push({ orgName: principalName?.trim() ?? principalName, uen: uen?.trim() ?? uen });
		setPrincipalName('');
		setUEN('');
		setNewPrincipals(array);
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		goTo(-1)
	}

	/*=================================================
		Data Table
	/================================================*/

	function deleteFromList(index) {
		const array = [...newPrincipals];
		if (index !== -1) {
			array.splice(index, 1);
			setNewPrincipals(array);
		}
	}

	function clearInputs() {
		setPrincipalName('');
		setUEN('');
	}

	const DataTable = ({ itemData }) => {
		const list = itemData.map((item, index) => {
			return (
				<div className="data-table__table__body__row" key={index}>
					<div
						className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--break data-table__flex"
						style={{ display: "flex", alignItems: "center" }}
					>
						{item.orgName}
					</div>
					<div
						className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--break data-table__flex"
						style={{ display: "flex", alignItems: "center" }}
					>
						{item.uen}
					</div>
					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--end">
						{!bulkUploadFile && (
							<span
								data-testid="delete-principal"
								className="link"
								onClick={() => {
									deleteFromList(index);
								}}
							>
								<IconDelete />
							</span>
						)}
					</div>
				</div>
			);
		});

		return (
			<div className="data-table__content agent_principal__add_principal__data_table">
				<div className="data-table__content__cols">
					<div className="data-table__content__col">
						<div className="data-table__table">
							<div className="data-table__table__head">
								<div
									className="data-table__table__head__row"
									key="head"
								>
									<div
										className={`data-table__table__head__title  data-table__table__head__title`}
										id="name"
									>
										Principal Name
									</div>
									<div
										className={`data-table__table__head__title  data-table__table__head__title`}
										id="uen"
									>
										UEN
									</div>
									<div className="data-table__table__head__title data-table__header data-table__table__head__title--end"></div>
								</div>
							</div>
							<div className="data-table__table__body">
								{list}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	async function confirm() {
		if (loading) {
			return
		}

		const result = await create(newPrincipals.map(s => ({ name: s.orgName, uen: s.uen })))

		if (result) {
			onBack();
		}
	}

	/*=================================================
		Modal
	/================================================*/

	const { hide: hideErrorModal, show: showErrorModal, visible: visibleErrorModal, } = useModal();
	const { hide: hideDeleteModal, show: showDeleteModal, visible: visibleDeleteModal, } = useModal();
	const { hide: hideUploadingErrorModal, show: showUploadingErrorModal, visible: visibleUploadingErrorModal, } = useModal();

	/*=================================================
		Dropzone
	/================================================*/

	const dropzoneRef = useRef(null);
	const [uploadingError, setUploadingError] = useState({ failed: 0, success: 0, total: 0, failedList: [] })

	async function readCSV(file) {
		const fileReader = new FileReader();
		const content = await new Promise((resolve, reject) => {
			fileReader.onload = e => resolve(e.target.result);
			fileReader.onerror = e => reject(e);
			fileReader.readAsText(file, "utf-8");
		});

		// Check if file is valid, verify it's header
		const checkHeader = new RegExp(/^Principal Name,UEN\r?\n/)
		if (!checkHeader.test(content)) {
			return showErrorModal();
		}

		const convertedJSON = await convertCSVtoJSON(content);
		return convertedJSON;
	}

	async function checkUploadedFileForError(jsonArr) {
		let error = [];
		let result = [];

		if (jsonArr?.length) {
			// Fetch matching UENs;
			const foundResult = await find({ uen: jsonArr.map(s => s.uen) });

			for (let [k, v] of Object.entries(jsonArr)) {
				// Check if file has orgName
				if (!v.orgName) {
					error.push({
						line: Number(k) + 1,
						reason: 'Principal Name is required'
					})
					continue;
				}
				// Check if file has uen
				if (!v.uen) {
					error.push({
						line: Number(k) + 1,
						reason: 'UEN is required'
					})
					continue;
				} else
					// Check if uen is valid
					if (isAgentPrincipalPhase2Enabled ? !validatePrincipalUEN(v.uen) : !validateUEN(v.uen, 'principal')) {
						error.push({
							line: Number(k) + 1,
							reason: 'Invalid UEN'
						})
						continue;
					}
				// Check if file has duplicate UEN
				if (result?.find(s => s.uen == v.uen)) {
					error.push({
						line: Number(k) + 1,
						reason: 'Duplicate UEN'
					})
					continue;
				}

				// Check if uen is already in the system
				if (foundResult?.find(s => s.uen == v.uen)) {
					error.push({
						line: Number(k) + 1,
						reason: 'UEN already registered'
					})
					continue;
				}

				result.push({ ...v });
			}

			if (error?.length) {
				// show error Modal
				setUploadingError({ failed: jsonArr?.length - result?.length, success: result?.length, total: jsonArr?.length, failedList: error })
				showUploadingErrorModal()
				return result;
			} else {
				return result
			}
		}
	}

	async function callbackDropzone(files) {
		if (files?.length && files?.length !== 1) {
			setBulkUploadFile(false);
			setNewPrincipals([]);
			// addToastMessage('Please upload only 1 file', 'error');
			showErrorModal();
		}

		const file = files?.[0];
		let json;

		if (file?.type === "text/csv" || file?.type === "application/vnd.ms-excel") {
			json = await readCSV(file);
		}

		if (json?.length) {
			json = json?.map(s => ({ orgName: s['Principal Name'], uen: s['UEN'] }))
			setBulkUploadFile(file);
			const result = await checkUploadedFileForError(json);
			setNewPrincipals(result)
		} else {
			setBulkUploadFile(false);
			setNewPrincipals([]);
		}
	}

	const handleDownloadTemplate = async (e) => {
		e.stopPropagation();
		const csv = await convertJsonToCSV([{
			"Principal Name": "Example 1",
			"UEN": "201401010E",
		}, {
			"Principal Name": "Example 2",
			"UEN": "201401010A",
		}]);

		const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
		FileSaver.saveAs(blob, "sample_principal_bulk_upload.csv");
	}

	const handleDelete = () => {
		setBulkUploadFile(false);
		setNewPrincipals([]);
		dropzoneRef?.current?.clearFiles();
		hideDeleteModal();

		hideUploadingErrorModal();
		setUploadingError({ failed: 0, success: 0, total: 0, failedList: [] });
	}

	const handleUploadingErrorProceed = () => {
		setUploadingError({ failed: 0, success: 0, total: 0, failedList: [] });
		hideUploadingErrorModal();
	}

	const renderUploadingErrorList = (list) => {
		return list?.length && list?.map((item, index) => (
			<div className="agent_principal__uploading_error__table_row" key={index}>
				<div className="col1">
					{item.line}.
				</div>
				<div className="col2">
					{item.reason}
				</div>
			</div>
		))
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
				<div className="title-header__right">
				</div>
			</div>

			<hr />

			{input === INPUT.MANUAL && (
				<div className="agent_principal__content_col">
					<>
						<div className="agent_principal">
							<div className="agent_principal_fields__cols">
								<div className="agent_principal_fields__col">
									<div className="agent_principal_fields__col__top agent_principal_fields__col__title">
										Add Principal Name
									</div>
									<div className="agent_principal_fields__col__bottom">
										<input
											type="text"
											id="orgName"
											placeholder="Principal Name"
											value={principalName}
											onChange={(e) =>
												setPrincipalName(e.target.value)
											}
											disabled={loading}
										/>
										{internalError && (
											<div className="t--required">
												{internalError}
											</div>
										)}
									</div>
								</div>
								<div className="agent_principal_fields__col">
									<div className="agent_principal_fields__col__top agent_principal_fields__col__title">
										UEN
									</div>
									<div className="agent_principal__col__bottom agent_principal__col__bottom--background">
										<input
											type="text"
											id="uen"
											placeholder="Principal UEN"
											value={uen}
											onChange={(e) =>
												setUEN(e.target.value)
											}
											disabled={loading}
										/>
									</div>
								</div>
								<div className="agent_principal_fields__col">
									<div className="agent_principal_fields__col__top agent_principal_fields__col__title">
										&nbsp;
									</div>
									<div className="agent_principal_fields__col__bottom">
										<Button
											disabled={
												!(
													principalName && uen && (isAgentPrincipalPhase2Enabled ? validatePrincipalUEN(uen) : validateUEN(uen, 'SG'))
												)
											}
											text="Add"
											round
											submit={() =>
												handleSubmit()
											}
											loading={loading}
										/>
									</div>
								</div>
								<div className="agent_principal_fields__col">
									<div className="agent_principal_fields__col__top agent_principal_fields__col__title">
										&nbsp;
									</div>
									<div className="agent_principal__col__bottom agent_principal__col__bottom--background">
										<Button
											text="Clear"
											round
											ghost
											submit={() => clearInputs()}
										/>
									</div>
								</div>
							</div>
						</div>
					</>
				</div>
			)}

			{input === INPUT.BULK && (
				<div className="agent_principal__content_col agent_principal__add_principal_bulk">
					<span className="agent_principal__subtitle">You may bulk upload your files by :</span>
					<div className="agent_principal__add_principal_bulk__upload">
						<AttachmentDropzone
							ref={dropzoneRef}
							// disabled={!requiredAttachments || requiredAttachments.length === 0}
							addClass="agent_principal__add_principal_bulk__upload__dropzone"
							title={
								<div className="agent_principal__add_principal_bulk__upload__title">
									<div className="agent_principal__add_principal_bulk__upload__title_left">
										Upload Data File
									</div>
									<div className="agent_principal__add_principal_bulk__upload__title_right">
										<div
											onClick={(e) => handleDownloadTemplate(e)}
										>
											Download <span>CSV Template</span>
										</div>
									</div>
								</div >
							}
							dropzoneTitle={<p>Drop your Data File or <span className="t-primary-color">Upload</span></p>}
							footnote={[""]}
							parentCallback={callbackDropzone}
							showAttachmentsContent={false}
							showReplace={true}
							showDelete={true}
							handleOnDeleteClick={() => showDeleteModal()}
							footer={
								<div>
									Acceptable File Types: CSV - UTF-8
								</div>
							}
							isLoading={loading}
							multiple={false}
							accept={'text/csv'}
						/>
					</div>
				</div>
			)}

			<div className="agent_principal__content_col overview__content">
				<span className="agent_principal__subtitle">Adding Principals{newPrincipals.length ? ` (${newPrincipals.length})` : ''}</span>
				<DataTable itemData={!uploadingError?.failedList || uploadingError?.failedList?.length === 0 ? newPrincipals : []} />
			</div>
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							text="Cancel"
							ghost
							round
							noborder
							submit={() => onBack()}
						/>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						<div className="layout-default--margin-right">
						</div>
						<Button text="Save" id="add-new-agent-principal-submit-id" data-testid="add-new-agent-principal-submit-id" submit={() => confirm()} full round disabled={loading} loading={loading} />
					</div>
				</div>
			</div>

			<ModalTemplate
				addClass="agent_principal__modal agent_principal__modal_error invalid_file"
				visible={visibleErrorModal}
				hide={hideErrorModal}
				show={showErrorModal}
				title={"Invalid File"}
				message={
					<div className="agent_principal__error_modal__message">
						Invalid file uploaded. Please upload the correct .CSV file
					</div>
				}
			/>

			<ModalTemplate
				addClass="agent_principal__modal agent_principal__modal_error delete"
				visible={visibleDeleteModal}
				hide={hideDeleteModal}
				show={showDeleteModal}
				title={"Delete File"}
				message={
					<div className="agent_principal__error_modal__message">
						Are you sure you want to delete the uploaded file?
					</div>
				}
				leftFooterAction={<Button ghost noborder round text="Cancel" submit={() => hideDeleteModal()} />}
				rightFooterAction={<Button full round text="Delete" submit={() => handleDelete()} />}
			/>

			<ModalTemplate
				addClass="agent_principal__modal agent_principal__modal_error uploading_error"
				visible={visibleUploadingErrorModal}
				hide={hideUploadingErrorModal}
				show={showUploadingErrorModal}
				title={"Uploading Error"}
				message={<div className="agent_principal__uploading_error__message_box">
					<div className="agent_principal__uploading_error__subtitle">
						<span>{`${uploadingError.failed}`} records out of {`${uploadingError.total}`} <b>Failed</b> to upload to the system, please check the rejected item below. {uploadingError.failed === uploadingError.total ? "Please Cancel and upload a new file." : "Proceed to upload the remaining successful records or Cancel to upload a new file."}</span>
					</div>
					<div className="agent_principal__uploading_error__table_title">
						Failed items ({uploadingError.failed})
					</div>
					<div className="agent_principal__uploading_error__table">
						<div className="agent_principal__uploading_error__table_header">
							<div className="col1">
								Item No.
							</div>
							<div className="col2">
								Rejected Reason
							</div>
						</div>
						<div className="agent_principal__uploading_error__table_row_scrollable">
							{renderUploadingErrorList(uploadingError?.failedList)}
						</div>
					</div>
				</div>}
				leftFooterAction={<Button ghost round text="Cancel" data-testid="uploading-error-cancel" submit={() => handleDelete()} />}
				rightFooterAction={<Button full disabled={uploadingError.failed === uploadingError.total} round text="Proceed" submit={() => handleUploadingErrorProceed()} />}
			/>

			<Toast toastList={toastList} />
		</>
	)
}

CreatePrincipal.propTypes = {
	input: PropTypes.string.isRequired,
}

export default CreatePrincipal;
